import {
  DisplayCategoryKey,
  JobTypeKey,
  ProductWeights,
  buildFilters,
  type Filter,
  type GroupPromptsFilters,
  type ProductType,
  type SavedPromptsFilters,
} from "@1js/pl-card-components";
import { type Group, type Prompt, type Status } from "@1js/pl-types";
import React, { useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { deserializeFilters } from "../../../components/AllPrompts/AllPrompts.web.data";
import type { WebPrompt } from "../../../components/data";
import type { PromptReference } from "../../../services/prompt-client.web";
import { sendClientLayoutEvent } from "../../../utils/Telemetry";

const pageSize = 16;

export type PromptLibraryState = {
  allPrompts: Prompt[];
  allFilteredPrompts: Prompt[];
  displayedPrompts: Prompt[];
  moreLikeThis: WebPrompt[];
  selectedFacets: Record<string, string[]>;
  initSearchParams: URLSearchParams;
  filters: Filter[];

  hasMore?: boolean;
  showBookmarkOnly: boolean;
  showGroupPromptsOnly: boolean;
  hasBookmarkedPrompts?: boolean;
  savedPromptsFilter: SavedPromptsFilters;
  groupPromptsFilter: GroupPromptsFilters;
  groups?: Group[];
  groupPromptStatus: Status;
  groupPrompts: Record<string, Prompt[]>;
  displayedGroupPrompts: Prompt[];

  groupHasPromptsStatus: Status;
  groupOwnedStatus: Status;
  groupsHasPrompts?: Group[];
  groupsHasPromptsInit?: boolean;
  groupsOwned?: Group[];
  groupsOwnedInit?: boolean;
  serviceHost: string;
  locale: string;
};

export const initialState: PromptLibraryState = {
  allPrompts: [],
  allFilteredPrompts: [],
  displayedPrompts: [],
  moreLikeThis: [],
  selectedFacets: {},
  filters: [],
  showBookmarkOnly: false,
  showGroupPromptsOnly: false,
  savedPromptsFilter: "CreatedByAll",
  groupPromptsFilter: "Default",
  groups: undefined,
  groupPrompts: {},
  groupPromptStatus: "idle",
  displayedGroupPrompts: [],
  initSearchParams: new URLSearchParams(),
  groupHasPromptsStatus: "idle",
  groupOwnedStatus: "idle",
  groupsHasPrompts: undefined,
  groupsHasPromptsInit: false,
  groupsOwned: undefined,
  groupsOwnedInit: false,
  serviceHost: "https://copilot.cloud.microsoft/",
  locale: "",
};

export type PromptLibraryAction =
  | {
      type: "SET_PROMPTS";
      payload: { prompts: Prompt[] };
    }
  | {
      type: "SET_GROUP_PROMPTS_ERROR";
      payload: { groupId: string; groups: undefined };
    }
  | {
      type: "SET_GROUP_PROMPTS";
      payload: { groupId: string; groupPrompts: Prompt[] };
    }
  | {
      type: "INVALIDATE_GROUP_PROMPTS";
      payload: { groupIds: string[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_SUCCESS";
      payload: {
        groupIds: string[];
        promptsToBeDeleted: Prompt[];
        deletedPrompts: PromptReference[];
      };
    }
  | {
      type: "PUBLISH_GROUP_PROMPT_SUCCESS";
      payload: {
        requestedGroupIds: string[];
        requestedPrompts: Prompt[];
        publishedPromptIds: PromptReference[];
      };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_INIT";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_LONG";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_FAILED";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "SET_SELECTED_FACETS";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "LOAD_MORE";
    }
  | {
      type: "BOOKMARK_INIT";
      payload: { prompt: Prompt };
    }
  | {
      type: "BOOKMARK_SUCCEED";
      payload: { prompt: Prompt; value: boolean };
    }
  | {
      type: "BOOKMARK_FAILED";
      payload: { prompt: Prompt };
    }
  | {
      type: "MORE_LIKE_THIS";
      payload: { prompt: WebPrompt };
    }
  | {
      type: "TOGGLE_BOOKMARK_ONLY";
    }
  | {
      type: "SET_BOOKMARK_ONLY";
      payload: { bookmarkOnly: boolean; groupPromptsOnly: boolean };
    }
  | {
      type: "SET_SAVED_PROMPTS_FILTER";
      payload: { filter: SavedPromptsFilters };
    }
  | {
      type: "SET_GROUP_PROMPTS_FILTER_SUCCESS";
      payload: { filter: GroupPromptsFilters };
    }
  | {
      type: "JUMP_TO_GROUP_PROMPTS_GROUP";
      payload: { groupId: GroupPromptsFilters };
    }
  | {
      type: "SET_GROUP_PROMPTS_FILTER_FAILED";
      payload: { filter: GroupPromptsFilters };
    }
  | {
      type: "SET_GROUP_PROMPTS_FILTER_INIT";
      payload: { filter: GroupPromptsFilters };
    }
  | {
      type: "DELETE_UNDO";
      payload: { prompt: Prompt; promptPosition: number };
    }
  | {
      type: "DELETE_SUCCEED";
      payload: { prompt: Prompt };
    }
  | {
      type: "REQUEST_ALL_GROUPS_OWNED_INIT";
    }
  | {
      type: "REQUEST_ALL_GROUPS_OWNED_SUCCEED";
      payload: { groups: Group[]; groupId?: string };
    }
  | {
      type: "REQUEST_ALL_GROUPS_OWNED_FAILED";
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_INIT";
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED";
      payload: { groups: Group[]; groupId?: string };
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED";
    };

const filterGroupPrompts = (
  prompts: Prompt[] | undefined,
  _groupPromptsFilter: GroupPromptsFilters
) => {
  return (
    prompts?.filter((prompt: Prompt) =>
      prompt.Groups?.some((group: Group) => group.Id === _groupPromptsFilter)
    ) || []
  );
};

const filterPrompts = (
  prompts: Prompt[],
  filters: Record<string, string[]>,
  favoritesOnly: boolean,
  savedPromptsFilter: SavedPromptsFilters
) => {
  let filteredPrompts = favoritesOnly
    ? prompts.filter(
        (p) => p.UserActivity.Favorite.IsFavorite || p.Origin === "User"
      )
    : prompts.filter((p) => p.Origin !== "User");

  // Save prompts filter
  if (favoritesOnly && savedPromptsFilter === "CreatedByMe") {
    filteredPrompts = filteredPrompts.filter((p) => p.Origin === "User");
  } else if (favoritesOnly && savedPromptsFilter === "CreatedByMicrosoft") {
    filteredPrompts = filteredPrompts.filter((p) => p.Origin !== "User");
  }

  // Loop through each key-value pair in the filters object
  for (const [key, values] of Object.entries(filters)) {
    switch (key) {
      case DisplayCategoryKey:
        filteredPrompts =
          values.length > 0
            ? filteredPrompts.filter((f) => values.includes(f.DisplayCategory))
            : filteredPrompts;
        break;
      case "products/name":
        filteredPrompts =
          values.length > 0
            ? filteredPrompts.filter((f) =>
                values.some((item) => f.Products.includes(item))
              )
            : filteredPrompts;
        break;
      case JobTypeKey:
        filteredPrompts =
          values.length > 0
            ? filteredPrompts.filter((f) =>
                values.some((item) =>
                  f.Metadata?.find((m) => {
                    return m.Value === item;
                  })
                )
              )
            : filteredPrompts;
        break;
    }
  }

  return filteredPrompts;
};
/**
 * Sort product type filters by weight
 * @param filters
 */
const sortProductTypeFilters = (filters: Filter[]): void => {
  filters.forEach((filter) => {
    if (filter.key === "products/name") {
      filter.options = filter.options.sort((a, b) => {
        const aWeight = ProductWeights[a.value as ProductType] || 0;
        const bWeight = ProductWeights[b.value as ProductType] || 0;
        return aWeight - bWeight;
      });
    }
  });
};

const reducer = (
  state: PromptLibraryState,
  action: PromptLibraryAction
): PromptLibraryState => {
  switch (action.type) {
    case "SET_GROUP_PROMPTS": {
      const groupPrompts: Record<string, Prompt[]> = { ...state.groupPrompts };
      groupPrompts[action.payload.groupId] = action.payload.groupPrompts;

      return {
        ...state,
        groupPromptStatus: "fulfilled",
        groupPrompts,
        displayedGroupPrompts: filterGroupPrompts(
          groupPrompts[action.payload.groupId],
          state.groupPromptsFilter
        ),
      };
    }
    case "INVALIDATE_GROUP_PROMPTS": {
      const groupPrompts: Record<string, Prompt[]> = { ...state.groupPrompts };
      action.payload.groupIds.forEach((groupId) => {
        delete groupPrompts[groupId];
      });
      return {
        ...state,
        groupPrompts,
      };
    }
    case "REMOVE_GROUP_PROMPT_SUCCESS": {
      action.payload.promptsToBeDeleted.forEach((prompt) => {
        prompt.RemovingState = "false";
      });

      const groupPrompts: Record<string, Prompt[]> = { ...state.groupPrompts };
      const deletedPromptSet = new Set(
        action.payload.deletedPrompts.map((prompt) => prompt.PromptId)
      );
      action.payload.groupIds.forEach((groupId) => {
        groupPrompts[groupId] = groupPrompts[groupId]?.filter(
          (prompt) => !deletedPromptSet.has(prompt.PromptId)
        );
      });
      return {
        ...state,
        groupPrompts,
        displayedGroupPrompts: filterGroupPrompts(
          groupPrompts[state.groupPromptsFilter],
          state.groupPromptsFilter
        ),
      };
    }
    case "PUBLISH_GROUP_PROMPT_SUCCESS": {
      const groupPrompts: Record<string, Prompt[]> = { ...state.groupPrompts };
      let promptIndex = 0;
      action.payload.requestedPrompts.forEach((prompt) => {
        action.payload.requestedGroupIds.forEach((groupId) => {
          const sharedPrompt: Prompt = JSON.parse(JSON.stringify(prompt));
          sharedPrompt.Groups = sharedPrompt.Groups || [];
          const group = state.groups?.find((group) => group.Id === groupId);
          if (group) {
            sharedPrompt.Groups.push(group);
          }

          if (groupPrompts[groupId]) {
            const id = action.payload.publishedPromptIds[promptIndex].PromptId;
            const promptId = id.split("_")[0];
            groupPrompts[groupId].splice(0, 0, {
              ...sharedPrompt,
              Id: id,
              PromptId: promptId,
            });
          }
          promptIndex++;
        });
      });
      return {
        ...state,
        groupPrompts,
        displayedGroupPrompts: filterGroupPrompts(
          groupPrompts[state.groupPromptsFilter],
          state.groupPromptsFilter
        ),
      };
    }
    case "REMOVE_GROUP_PROMPT_INIT": {
      action.payload.prompts.forEach((prompt) => {
        prompt.RemovingState = "true";
      });

      return {
        ...state,
      };
    }
    case "REMOVE_GROUP_PROMPT_LONG": {
      action.payload.prompts.forEach((prompt) => {
        if (prompt.RemovingState === "true") {
          prompt.RemovingState = "long";
        }
      });

      return {
        ...state,
      };
    }
    case "REMOVE_GROUP_PROMPT_FAILED": {
      action.payload.prompts.forEach((prompt) => {
        prompt.RemovingState = "false";
      });

      return {
        ...state,
      };
    }
    case "SET_PROMPTS": {
      // we always want to display the user-authored prompts before Microsoft created ones
      const allPrompts = action.payload.prompts.sort((prompt) =>
        prompt.Origin === "User" ? -1 : 1
      );
      const filteredPrompts = filterPrompts(
        allPrompts,
        state.selectedFacets,
        state.showBookmarkOnly,
        state.savedPromptsFilter
      );
      const displayedPrompts = filteredPrompts.slice(0, pageSize);

      sendClientLayoutEvent([...displayedPrompts], "Vertical");
      const filters = buildFilters(allPrompts);
      sortProductTypeFilters(filters);

      return {
        ...state,
        allPrompts: [...allPrompts],
        allFilteredPrompts: [...filteredPrompts],
        displayedPrompts,
        filters: filters,
        selectedFacets: deserializeFilters(state.initSearchParams, filters),
        hasMore: allPrompts.length > pageSize,
        hasBookmarkedPrompts: allPrompts.some(
          (x) => x.UserActivity.Favorite.IsFavorite || x.Origin === "User"
        ),
      };
    }
    case "SET_SELECTED_FACETS": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        action.payload.facets,
        state.showBookmarkOnly,
        state.savedPromptsFilter
      );
      return {
        ...state,
        selectedFacets: action.payload.facets,
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, 16),
        hasMore: filteredPrompts.length > pageSize,
      };
    }
    case "LOAD_MORE": {
      const newDisplayedPrompts = state.allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length + pageSize
      );

      sendClientLayoutEvent([...newDisplayedPrompts], "Vertical");

      return {
        ...state,
        displayedPrompts: newDisplayedPrompts,
        hasMore: newDisplayedPrompts.length < state.allFilteredPrompts.length,
      };
    }
    case "BOOKMARK_INIT":
      action.payload.prompt.IsBookmarking = true;
      return {
        ...state,
        allPrompts: [...state.allPrompts],
        displayedPrompts: [...state.displayedPrompts],
      };
    case "BOOKMARK_SUCCEED": {
      action.payload.prompt.IsBookmarking = false;
      action.payload.prompt.UserActivity.Favorite.IsFavorite =
        action.payload.value;
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        state.showBookmarkOnly,
        state.savedPromptsFilter
      );
      return {
        ...state,
        allPrompts: [...state.allPrompts],
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: state.showBookmarkOnly
          ? state.displayedPrompts.filter(
              (x) => x.UserActivity.Favorite.IsFavorite
            )
          : [...state.displayedPrompts],
        hasBookmarkedPrompts: state.allPrompts.some(
          (x) => x.UserActivity.Favorite.IsFavorite || x.Origin === "User"
        ),
      };
    }
    case "BOOKMARK_FAILED":
      action.payload.prompt.IsBookmarking = false;
      return {
        ...state,
        allPrompts: [...state.allPrompts],
        displayedPrompts: [...state.displayedPrompts],
      };
    case "MORE_LIKE_THIS":
      return {
        ...state,
        moreLikeThis: state.allPrompts
          .filter(
            (x) =>
              x.DisplayCategory === action.payload.prompt.DisplayCategory &&
              x.Id !== action.payload.prompt.Id
          )
          .slice(0, 2) as WebPrompt[],
      };
    case "TOGGLE_BOOKMARK_ONLY": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        !state.showBookmarkOnly,
        state.savedPromptsFilter
      );
      return {
        ...state,
        showBookmarkOnly: !state.showBookmarkOnly,
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, 16),
        hasMore: filteredPrompts.length > pageSize,
      };
    }
    case "SET_BOOKMARK_ONLY": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        action.payload.bookmarkOnly,
        state.savedPromptsFilter
      );
      return {
        ...state,
        showBookmarkOnly: action.payload.bookmarkOnly,
        showGroupPromptsOnly: action.payload.groupPromptsOnly,
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, 16),
        hasMore: filteredPrompts.length > pageSize,
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts[state.groupPromptsFilter],
          state.groupPromptsFilter
        ),
      };
    }
    case "SET_SAVED_PROMPTS_FILTER": {
      const filteredPrompts = filterPrompts(
        state.allPrompts,
        state.selectedFacets,
        state.showBookmarkOnly,
        action.payload.filter
      );
      return {
        ...state,
        savedPromptsFilter: action.payload.filter,
        allFilteredPrompts: filteredPrompts,
        displayedPrompts: filteredPrompts.slice(0, pageSize),
        hasMore: filteredPrompts.length > pageSize,
      };
    }
    case "SET_GROUP_PROMPTS_FILTER_INIT": {
      return {
        ...state,
        groupPromptsFilter: action.payload.filter,
        groupPromptStatus: "loading",
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts[action.payload.filter],
          action.payload.filter
        ),
      };
    }
    case "SET_GROUP_PROMPTS_FILTER_SUCCESS": {
      return {
        ...state,
        groupPromptsFilter: action.payload.filter,
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts[action.payload.filter],
          action.payload.filter
        ),
        groupPromptStatus: "fulfilled",
      };
    }
    case "JUMP_TO_GROUP_PROMPTS_GROUP": {
      return {
        ...state,
        groupPromptsFilter: action.payload.groupId,
        showBookmarkOnly: false,
        showGroupPromptsOnly: true,
        displayedGroupPrompts: filterGroupPrompts(
          state.groupPrompts[action.payload.groupId],
          action.payload.groupId
        ),
      };
    }
    case "SET_GROUP_PROMPTS_FILTER_FAILED": {
      return {
        ...state,
        groupPromptsFilter: action.payload.filter,
        displayedGroupPrompts: [],
        groupPromptStatus: "failed",
      };
    }
    case "DELETE_UNDO": {
      const allPrompts = [...state.allPrompts];

      // make sure the prompt is not already in the list
      if (
        allPrompts.findIndex(
          (prompt) => prompt.Id === action.payload.prompt.Id
        ) === -1
      ) {
        allPrompts.splice(
          action.payload.promptPosition,
          0,
          action.payload.prompt
        );
      }

      const allFilteredPrompts = filterPrompts(
        allPrompts,
        state.selectedFacets,
        state.showBookmarkOnly,
        state.savedPromptsFilter
      );
      const displayedPrompts = allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length + 1
      );

      return {
        ...state,
        allPrompts,
        allFilteredPrompts,
        displayedPrompts,
        hasMore: allFilteredPrompts.length > pageSize,
      };
    }
    case "DELETE_SUCCEED": {
      const allPrompts = [...state.allPrompts].filter(
        (prompt) => prompt.Id !== action.payload.prompt.Id
      );
      const allFilteredPrompts = filterPrompts(
        allPrompts,
        state.selectedFacets,
        state.showBookmarkOnly,
        state.savedPromptsFilter
      );
      const displayedPrompts = allFilteredPrompts.slice(
        0,
        state.displayedPrompts.length
      );

      return {
        ...state,
        allPrompts,
        allFilteredPrompts,
        displayedPrompts,
        hasMore: allFilteredPrompts.length > pageSize,
      };
    }
    case "REQUEST_ALL_GROUPS_OWNED_INIT":
      return {
        ...state,
        groupOwnedStatus: "loading",
      };
    case "REQUEST_ALL_GROUPS_OWNED_SUCCEED": {
      const sortedOwnedGroups: Group[] = action.payload.groups.sort(
        (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
      );

      const groups: Group[] = state.groups
        ? [...(state.groups as Group[])]
        : [];
      sortedOwnedGroups.forEach((group) => {
        const existingGroup = groups.find((g) => g.Id === group.Id);
        if (!existingGroup) {
          groups.push(group);
        } else {
          existingGroup.IsOwner = true;
        }
      });
      groups.forEach((group) => {
        // it's possible user lost group ownership changed since last fetch
        if (!sortedOwnedGroups.find((g) => g.Id === group.Id)) {
          group.IsOwner = false;
        }
      });

      const sortedGroups =
        groups?.sort((a: Group, b: Group) =>
          a.DisplayName.localeCompare(b.DisplayName)
        ) ?? [];

      return {
        ...state,
        groupOwnedStatus: "fulfilled",
        groupsOwnedInit: true,
        groupsOwned: sortedOwnedGroups,
        groups: sortedGroups,
      };
    }
    case "REQUEST_ALL_GROUPS_OWNED_FAILED":
      return {
        ...state,
        groupOwnedStatus: "failed",
        groupsOwnedInit: true,
        groupsOwned: undefined,
      };
    case "REQUEST_ALL_GROUPS_HASPROMPTS_INIT":
      return {
        ...state,
        groupHasPromptsStatus: "loading",
      };
    case "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED": {
      const sortedHasPromptsGroups: Group[] = action.payload.groups.sort(
        (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
      );

      const groups: Group[] = state.groups
        ? [...(state.groups as Group[])]
        : [];
      sortedHasPromptsGroups.forEach((group) => {
        const existingGroup = groups.find((g) => g.Id === group.Id);
        if (!existingGroup) {
          groups.push(group);
        } else {
          existingGroup.HasPrompts = true;
        }
      });
      groups.forEach((group) => {
        // it's possible prompts were removed from a group since last fetch
        if (!sortedHasPromptsGroups.find((g) => g.Id === group.Id)) {
          group.HasPrompts = false;
        }
      });

      const sortedGroups =
        groups?.sort((a: Group, b: Group) =>
          a.DisplayName.localeCompare(b.DisplayName)
        ) ?? [];

      return {
        ...state,
        groupHasPromptsStatus: "fulfilled",
        groupsHasPromptsInit: true,
        groupsHasPrompts: sortedHasPromptsGroups,
        groups: sortedGroups,
      };
    }
    case "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED":
      return {
        ...state,
        groupHasPromptsStatus: "failed",
        groupsHasPromptsInit: true,
        groupsHasPrompts: undefined,
      };
    default:
      return state;
  }
};

export type UseAllPromptsReducerProps = {
  serviceHost: string;
  locale: string;
};

function useAllPrompts({ serviceHost, locale }: UseAllPromptsReducerProps) {
  const [searchParams] = useSearchParams();
  const initialStatePopulated = React.useMemo(() => {
    return {
      ...initialState,
      initSearchParams: searchParams,
      serviceHost,
      locale,
    };
  }, [locale, searchParams, serviceHost]);

  const [state, dispatch] = useReducer(reducer, initialStatePopulated);

  return {
    state,
    dispatch,
  };
}

export default useAllPrompts;
